import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Upload, Cpu, Map, Search, FileBarChart, Zap } from 'lucide-react';
import AnimatedBackground from '../../components/AnimatedBackground';

const steps = [
  {
    icon: Upload,
    title: 'Upload',
    description: 'Scan and upload answer sheets or exam papers in any format',
    color: 'bg-blue-500'
  },
  {
    icon: Cpu,
    title: 'AI Evaluation',
    description: 'Our AI evaluates responses using board-standard marking schemes',
    color: 'bg-purple-500'
  },
  {
    icon: Map,
    title: 'Mapping',
    description: 'Each response is mapped to skills, topics, and competencies',
    color: 'bg-primary'
  },
  {
    icon: Search,
    title: 'Root Cause',
    description: 'Errors are classified and traced to their underlying causes',
    color: 'bg-amber-500'
  },
  {
    icon: FileBarChart,
    title: 'Diagnostic Report',
    description: 'Get a comprehensive report with actionable insights',
    color: 'bg-green-500'
  }
];

// Animated step component - NO ARROWS
function PipelineStep({ step, index, isLast }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const Icon = step.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.2, duration: 0.5 }}
      className="relative group"
    >
      {/* Step Icon - Static, no pulse */}
      <motion.div
        initial={{ scale: 0 }}
        animate={isInView ? { scale: 1 } : {}}
        transition={{ delay: index * 0.2 + 0.1, type: "spring", stiffness: 200 }}
        className={`w-12 h-12 rounded-full ${step.color} flex items-center justify-center mx-auto mb-4 shadow-lg relative z-10`}
      >
        <Icon className="w-6 h-6 text-white relative z-10" />
      </motion.div>
      
      {/* Content Card - Smaller */}
      <motion.div 
        className="text-center p-3 rounded-lg bg-card border border-border group-hover:border-primary/30 transition-all duration-300 relative z-10"
        whileHover={{ y: -3 }} 
      >
        <span className="inline-block px-2 py-0.5 rounded-full bg-muted text-xs font-mono mb-1">
          Step {index + 1}
        </span>
        <h3 className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors">{step.title}</h3>
        <p className="text-xs text-muted-foreground">{step.description}</p>
      </motion.div>
    </motion.div>
  );
}

// Mobile step component
function MobileStep({ step, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const Icon = step.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: index * 0.15, duration: 0.5 }}
      className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border group hover:border-primary/30 transition-colors"
      whileHover={{ x: 5 }}
    >
      <div className={`w-12 h-12 rounded-full ${step.color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-muted-foreground px-2 py-0.5 rounded bg-muted">
            Step {index + 1}
          </span>
        </div>
        <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">{step.title}</h3>
        <p className="text-sm text-muted-foreground">{step.description}</p>
      </div>
    </motion.div>
  );
}

export default function HowItWorksSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section 
      ref={sectionRef}
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-16 md:py-24 overflow-hidden"
    >
      {/* Subtle Background Animation */}
      <div className="absolute inset-0 opacity-15">
        <AnimatedBackground variant="subtle" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - CLEAN & STATIC */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Cpu className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">How It Works</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            From paper to
            <br />
            <span className="text-primary">precision diagnosis</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            A streamlined five-step process that transforms raw exam data into actionable intelligence.
          </p>
          
          <p className="text-sm font-mono text-primary inline-flex items-center gap-2">
            <Zap className="w-4 h-4" />
            No fluff. Exam-authentic logic only.
          </p>
        </motion.div>

        {/* Pipeline - Desktop */}
        <div className="hidden lg:block relative">
          {/* Static Connection Line Background */}
          <div className="absolute top-7 left-0 right-0 h-1 bg-border" />
          
          {/* Animated Fill Line */}
          <motion.div 
            className="absolute top-7 left-0 h-1 bg-gradient-to-r from-primary via-primary to-primary/50"
            initial={{ width: 0 }}
            animate={isInView ? { width: '100%' } : {}}
            transition={{ duration: 2, ease: "easeOut", delay: 0.5 }}
          />
          
          <div className="relative grid grid-cols-5 gap-4">
            {steps.map((step, index) => (
              <PipelineStep 
                key={index} 
                step={step} 
                index={index} 
                isLast={index === steps.length - 1}
              />
            ))}
          </div>
        </div>

        {/* Pipeline - Mobile */}
        <div className="lg:hidden space-y-4">
          {steps.map((step, index) => (
            <MobileStep key={index} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
